import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

/**
 * Universal video player.
 * Supports:
 *   - iframe embeds (YouTube, Facebook)
 *   - HLS streams (.m3u8) via hls.js — used for vMix / custom RTMP-to-HLS
 *   - Direct MP4/WebM files
 */
export default function VideoPlayer({ type, url, autoPlay = false, muted = false, title = "Vidéo" }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const isHls = (u) => typeof u === "string" && (u.includes(".m3u8") || type === "vmix");
  const useIframe = type === "youtube" || type === "facebook";

  useEffect(() => {
    if (useIframe || !url) return;
    const video = videoRef.current;
    if (!video) return;

    // Cleanup previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (isHls(url)) {
      if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
        hls.loadSource(url);
        hls.attachMedia(video);
        hlsRef.current = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS
        video.src = url;
      }
    } else {
      video.src = url;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [url, type, useIframe]);

  if (!url) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900 text-white/60 text-sm">
        Aucune URL configurée
      </div>
    );
  }

  if (useIframe) {
    return (
      <iframe
        src={url}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      autoPlay={autoPlay}
      muted={muted}
    />
  );
}
